// ResultsSummaryCard — Card résultats réutilisable (casques + score circulaire + Q1/Q2)
// Extraite de AttestationPage pour éviter la duplication

import { Card, CardContent } from '../ui'
import { CasqueIcons } from './CasqueDisplay'
import ScoreProgressBar from './ScoreProgressBar'
import type { SeuilCasque } from '@autodiag/shared'

interface ResultsSummaryCardProps {
  casques: number
  metiersSelectionnes: { code: string; nom: string }[]
  metierIds: string[]
  scoreGlobal: number
  maxGlobal: number
  scoreQ1: number
  maxQ1: number
  scoreQ2: number
  maxQ2: number
  seuilsActuels: SeuilCasque[]
  getNiveauLabel: (casques: number) => string
}

export default function ResultsSummaryCard({
  casques,
  metiersSelectionnes,
  metierIds,
  scoreGlobal,
  maxGlobal,
  scoreQ1,
  maxQ1,
  scoreQ2,
  maxQ2,
  seuilsActuels,
  getNiveauLabel,
}: ResultsSummaryCardProps) {
  const scoreGlobalPercent = maxGlobal > 0 ? Math.round((scoreGlobal / maxGlobal) * 100) : 0
  const scoreQ1Percent = maxQ1 > 0 ? Math.round((scoreQ1 / maxQ1) * 100) : 0
  const scoreQ2Percent = maxQ2 > 0 ? Math.round((scoreQ2 / maxQ2) * 100) : 0

  return (
    <Card className="overflow-hidden py-0">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-muted/50 border-b border-border">
        <div className="flex items-center gap-3">
          <span className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold">
            Résultats
          </span>
          <span className="text-border">|</span>
          <div className="flex items-center gap-2">
            {metiersSelectionnes.map((metier, index) => (
              <span
                key={metier.code}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-background border border-border text-foreground text-sm font-medium shadow-sm"
              >
                {metierIds.length > 1 && (
                  <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                    {index + 1}
                  </span>
                )}
                {metier.nom}
              </span>
            ))}
          </div>
        </div>
      </div>

      <CardContent className="p-5 sm:p-6">
        {/* Inner grid : casques + score — xl breakpoint pour éviter le tassement à 150% */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Section Casques - 2/3 à xl */}
          <div className="xl:col-span-2 flex flex-col justify-center">
            {/* 5 icônes casques */}
            <div className="flex justify-center mb-4">
              <CasqueIcons count={casques} size="lg" />
            </div>

            {/* Texte résultat casques */}
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-foreground">
                {casques} casque{casques > 1 ? 's' : ''} sur 5
              </div>
              <p className="text-sm text-primary font-medium mt-1">
                {getNiveauLabel(casques)}
              </p>
            </div>

            {/* Barre de progression avec seuils */}
            <div className="mt-5 w-full px-2">
              <ScoreProgressBar
                score={scoreGlobal}
                seuils={seuilsActuels}
                compact={true}
              />
            </div>
          </div>

          {/* Section Score Global - 1/3 à xl */}
          <div className="xl:border-l xl:border-border xl:pl-6 flex flex-col items-center justify-center">
            {/* Chart circulaire */}
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 mb-3">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="10" className="text-muted" />
                <circle
                  cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${(scoreGlobalPercent / 100) * 264} 264`}
                  className="text-primary transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl sm:text-2xl font-bold text-primary">
                  {scoreGlobalPercent}%
                </span>
              </div>
            </div>
            <div className="text-center mb-3">
              <span className="text-sm font-semibold text-foreground">Score Global</span>
              <div className="text-xs text-muted-foreground">
                {scoreGlobal} / {maxGlobal} pts
              </div>
            </div>

            {/* Séparateur + Q1/Q2 */}
            <div className="w-full h-px bg-border mb-3"></div>
            <div className="w-full space-y-2">
              {/* Q1 - Risques */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-semibold">Q1</span>
                    <span className="text-xs text-foreground">Risques</span>
                  </div>
                  <span className="text-xs font-bold text-blue-700">{scoreQ1Percent}%</span>
                </div>
                <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 transition-all" style={{ width: `${scoreQ1Percent}%` }} />
                </div>
              </div>

              {/* Q2 - Management */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded font-semibold">Q2</span>
                    <span className="text-xs text-foreground">Management</span>
                  </div>
                  <span className="text-xs font-bold text-purple-700">{scoreQ2Percent}%</span>
                </div>
                <div className="h-1.5 bg-purple-100 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 transition-all" style={{ width: `${scoreQ2Percent}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
